import React, { useEffect, useState } from 'react';
import './styles';
import { useDispatch, useSelector } from 'react-redux';
import Queries from '../../../../../../utils/API/Queries';
import { PlusIcon, Trashcan } from '../../../../../../assets/icons';
import UIListInputRow from '../../../../../../components/UI/UIListInputRow';
import { OPTION_TYPES } from '../../../../../../utils/Config/Constants';
import UIModal from '../../../../../../components/UI/UIModal';
import UICard from '../../../../../../components/UI/UICard/UICard';
import UIIconButton from '../../../../../../components/UI/UIIconButton';
import UIOverlayCard from '../../../../../../components/UI/UIOverlayCard';
import UIDropdownSelect from '../../../../../../components/UI/UIDropdownSelect';
import UIBookableOption from '../../../../../../components/UI/UIBookableOption';
import UIPartnerPackage from '../../../../../../components/UI/UIPartnerPackage/UIPartnerPackage';
import CreatePartnerPackageModal from '../../../../../../components/modals/CreatePartnerPackageModal';
import CreatePartner from './CreatePartner/CreatePartner';
import UIIcon from '../../../../../../components/UI/UIIcon';
import { UI_LABELED_INPUT_NUMBER_TYPE } from '../../../../../../components/UI/UILabeledInput/config';
import { getCurrentUserSelector, getSelectedRegionSelector } from '../../../../../../redux/selectors/CurrentUserSelectors';
import { TABS } from './config';
import { toast } from 'react-toastify';
import { RegionConfigProps } from './types';
import { useApolloClient } from '@apollo/react-hooks';
import { setSelectedRegion, showOption } from '../../../../../../redux/store/user/actions';
import * as strings from './strings';
import { CHILD_TYPE } from '../../../../../../components/UI/UIListInputRow/config';
import { UI_ICON_BUTTON_CANCEL_TYPE, UI_ICON_BUTTON_FILL_TYPE, UI_ICON_BUTTON_OUTLINE_TYPE } from '../../../../../../components/UI/UIIconButton/config';
import { useQuery } from 'react-apollo';

export default function RegionConfig({ selectedRegionId }: RegionConfigProps) {
	const currentUser = useSelector(getCurrentUserSelector);
	const selectedRegion = useSelector(getSelectedRegionSelector);
	const client = useApolloClient();
	const dispatch = useDispatch();

	const [addingPartner, setAddingPartner] = useState<boolean>(false);
	const [partnerToDelete, setPartnerToDelete] = useState<PartnerType | undefined>(undefined);
	const [showDeletePartnerModal, setShowDeletePartnerModal] = useState<boolean>(false);
	const [showCreatePackageForPartner, setShowCreatePackageForPartner] = useState<PartnerType | undefined>(undefined);
	const [selectedTab, setSelectedTab] = useState(TABS.REGION);
	const [name, setName] = useState<string>(selectedRegion.name);
	const [infoPath, setInfoPath] = useState<string>(selectedRegion.infoPath);
	const [flexBookingDelayTime, setFlexBookingDelayTime] = useState<number>(selectedRegion.flexBookingDelayTime);
	const [timezone, setTimezone] = useState<string>(selectedRegion.timezone);
	const [latitude, setLatitude] = useState<number>(selectedRegion.latitude);
	const [longitude, setLongitude] = useState<number>(selectedRegion.longitude);
	const [state, setState] = useState<string>(selectedRegion.state);
	const [regionValue, setRegionValue] = useState<RegionType>(selectedRegion);

	const { data: optionsData } = useQuery(Queries.GET_OPTIONS, {
		variables: {
			region: selectedRegionId,
			query: {
				type: { op_in: [OPTION_TYPES.twilightUpgrade, OPTION_TYPES.virtualTwilight] }
			}
		}
	});

	useEffect(() => {
		(async () => {
			setName(selectedRegion.name);
			setInfoPath(selectedRegion.infoPath);
			setFlexBookingDelayTime(selectedRegion.flexBookingDelayTime);
			setTimezone(selectedRegion.timezone);
			setLatitude(selectedRegion.latitude);
			setLongitude(selectedRegion.longitude);
			setState(selectedRegion.state);
			setRegionValue(selectedRegion);
		})();
	}, [selectedRegion]);

	//Mark: Component functions
	const onChangeOption = async (objKey, value) => {
		selectedRegion[objKey] = value;

		let variables = { id: selectedRegion.id };
		variables[objKey] = value;
		const response = await client
			.mutate({
				mutation: Queries.UPDATE_REGION,
				variables: variables
			})
			.catch((error) => {
				toast.error(error.message);
			});
		const { updateRegion } = response['data'];
		if (updateRegion) {
			dispatch(setSelectedRegion(selectedRegion));
			toast.success('Updated ' + objKey + '!');
		} else {
			toast.error(strings.errorUpdatingOption);
		}
	};

	const renderLightingOptions = () => {
		const options = optionsData?.options;

		if (!options) return null;

		let twilightOption = options.filter((option) => option.type === OPTION_TYPES.twilightUpgrade)[0];
		let twilightBookableOption: BookableOptionType = { option: twilightOption, selectedQuantity: 1 };
		let virtualTwilightOption = options.filter((option) => option.type === OPTION_TYPES.virtualTwilight)[0];
		let virtualTwilightBookableOption = { option: virtualTwilightOption, selectedQuantity: virtualTwilightOption.quantity };

		return (
			<div>
				<UIListInputRow label={strings.twilightLabel} type={CHILD_TYPE}>
					{twilightOption ? (
						<UIBookableOption
							className='uiOptionGroup__option'
							disableSelection
							bookableOption={twilightBookableOption}
							onClick={() => dispatch(showOption(twilightBookableOption.option.id))}
						/>
					) : (
						<div className='lightingOption'>
							<p className='lightingOption__notFound'>{strings.twilightText} </p>
						</div>
					)}
				</UIListInputRow>

				<UIListInputRow label={strings.virtualTwilightLabel} type={CHILD_TYPE}>
					{virtualTwilightOption ? (
						<UIBookableOption
							className='uiOptionGroup__option'
							disableSelection
							bookableOption={virtualTwilightBookableOption}
							onClick={() => dispatch(showOption(virtualTwilightBookableOption.option.id))}
						/>
					) : (
						<div className='lightingOption'>
							<p className='lightingOption__notFound'>{strings.virtualTwilightText}</p>
						</div>
					)}
				</UIListInputRow>
			</div>
		);
	};

	const renderCreatePackageForPartnerModal = () => {
		const hideModal = () => setShowCreatePackageForPartner(undefined);

		return (
			<UIModal condition={showCreatePackageForPartner !== undefined}>
				<CreatePartnerPackageModal partner={showCreatePackageForPartner} onCreateFinished={hideModal} />
			</UIModal>
		);
	};

	const renderDeletePartnerModal = () => {
		const { selectedRegion } = currentUser;
		const { regionConfig } = selectedRegion;

		const deletePartner = async () => {
			const { partners: partnersArray = [] } = regionConfig.coldwellPackages;
			if (!partnerToDelete || !currentUser.selectedRegion) {
				toast.error(strings.noPartnerToDelete);
			} else {
				const unchangedPartners = partnersArray.filter((filterPartner) => filterPartner.name !== partnerToDelete.name);

				const partners = [...unchangedPartners];
				let variables = {
					id: regionConfig.id,
					coldwellPackages: {
						...regionConfig.coldwellPackages,
						partners
					}
				};
				const response = await client
					.mutate({
						mutation: Queries.UPDATE_REGION_CONFIG,
						variables
					})
					.catch((error) => {
						toast.error(error.message);
					});
				const { updateRegionConfig } = response['data'];
				if (updateRegionConfig) {
					currentUser.selectedRegion.regionConfig = updateRegionConfig;
					dispatch(setSelectedRegion(currentUser.selectedRegion));
					hideModal();
				} else {
					toast.error(strings.errorUpdatingPackages);
				}
			}
		};
		const hideModal = () => {
			setPartnerToDelete(undefined);
			setShowDeletePartnerModal(false);
		};

		return (
			<UIModal condition={showDeletePartnerModal}>
				<UIOverlayCard hideModal={hideModal} title={strings.deletePartnerLabel}>
					<UIIconButton type={UI_ICON_BUTTON_CANCEL_TYPE} dark text={strings.deletePartnerButton} onClick={() => deletePartner()} icon={Trashcan.grey} />
				</UIOverlayCard>
			</UIModal>
		);
	};

	const renderTabPage = (selectedTab) => {
		switch (selectedTab.value) {
			case TABS.REGION.value:
				return renderRegionConfig();
			case TABS.PARTNERS.value:
				return renderPartnerConfig();
			default:
				return null;
		}
	};

	const renderRegionConfig = () => {
		return (
			<div className='regionConfig__config'>
				<UICard className='regionConfig__config--card'>
					<UIListInputRow
						helpText={strings.regionNameHelp}
						label={strings.regionNameLabel}
						placeholder={strings.regionNamePlaceholder}
						value={name}
						savedValue={regionValue.name}
						onChangeValue={setName}
						onSave={() => onChangeOption('name', name)}
					/>
					<UIListInputRow
						helpText={strings.regionFlexBookingHelp}
						label={strings.regionFlexBookingLabel}
						type={UI_LABELED_INPUT_NUMBER_TYPE}
						value={flexBookingDelayTime}
						savedValue={regionValue.flexBookingDelayTime}
						onChangeNumberValue={setFlexBookingDelayTime}
						onSave={() => onChangeOption('flexBookingDelayTime', flexBookingDelayTime)}
					/>
					<UIListInputRow
						helpText={strings.regionTimezoneHelp}
						label={strings.regionTimezoneLabel}
						placeholder={strings.regionTimezonePlaceholder}
						value={timezone}
						savedValue={regionValue.timezone}
						onChangeValue={setTimezone}
						onSave={() => onChangeOption('timezone', timezone)}
					/>
					<UIListInputRow
						helpText={strings.regionLatitudeHelp}
						label={strings.regionLatitudeLabel}
						type={UI_LABELED_INPUT_NUMBER_TYPE}
						placeholder={strings.regionLatitudePlaceholder}
						value={latitude}
						savedValue={regionValue.latitude}
						onChangeNumberValue={setLatitude}
						onSave={() => onChangeOption('latitude', latitude)}
					/>
					<UIListInputRow
						helpText={strings.regionLongitudeHelp}
						label={strings.regionLongitudeLabel}
						type={UI_LABELED_INPUT_NUMBER_TYPE}
						placeholder={strings.regionLongitudePlaceholder}
						value={longitude}
						savedValue={regionValue.longitude}
						onChangeNumberValue={setLongitude}
						onSave={() => onChangeOption('longitude', longitude)}
					/>
					<UIListInputRow
						helpText={strings.regionStateHelp}
						label={strings.regionStateLabel}
						placeholder={strings.regionStatePlaceholder}
						value={state}
						savedValue={regionValue.state}
						onChangeValue={setState}
						onSave={() => onChangeOption('state', state)}
					/>
					<UIListInputRow
						helpText={strings.regionInfoPathHelp}
						label={strings.regionInfoPathHelp}
						placeholder={strings.regionInfoPathPlaceholder}
						value={infoPath}
						savedValue={regionValue.infoPath}
						onChangeValue={setInfoPath}
						onSave={() => onChangeOption('infoPath', infoPath.replace(/[^A-Z\d]+/gi, '-').toLowerCase())}
					/>
				</UICard>
				<UICard className='regionConfig__config--card'>{renderLightingOptions()}</UICard>
			</div>
		);
	};

	const renderPartnerConfig = () => {
		const { selectedRegion } = currentUser;
		const { regionConfig } = selectedRegion;

		const { partners = [] } = regionConfig.coldwellPackages;

		return (
			<div className='regionConfig__partners'>
				<UIIconButton
					type={UI_ICON_BUTTON_FILL_TYPE}
					className='partnerCreateButton'
					dark
					text={strings.addNewPartnerButton}
					onClick={() => setAddingPartner(true)}
					icon={PlusIcon.grey}
				/>
				<div className='regionConfig__partners--content'>
					{partners.map((partner, key) => {
						const { packages = [] } = partner;
						return (
							<UICard
								key={key}
								title={`${partner.name}`}
								style={{ width: 396 }}
								rightItem={
									<UIIcon
										className='regionConfig__partners--content--icon'
										onClick={() => {
											setPartnerToDelete(partner);
											setShowDeletePartnerModal(true);
										}}
										iconSrc={Trashcan.white}
										alt={'delete'}
									/>
								}>
								<UIIconButton
									type={UI_ICON_BUTTON_OUTLINE_TYPE}
									onClick={() => setShowCreatePackageForPartner(partner)}
									icon={PlusIcon.white}
									style={{ margin: '24px 0 12px 0' }}
									text={strings.addNewPackageButton}
								/>
								{packages.map((partnerPackage, key) => (
									<UIPartnerPackage key={key} partner={partner} partnerPackage={partnerPackage} />
								))}
							</UICard>
						);
					})}
				</div>
			</div>
		);
	};

	const renderAddingPartnerModal = () => {
		const hideModal = () => {
			setAddingPartner(false);
		};

		const onCreateFinished = () => {
			toast.success(strings.successAddedPartner);
			hideModal();
		};

		return (
			<UIModal condition={addingPartner}>
				<UIOverlayCard hideModal={hideModal} title={strings.addNewPartnerLabel}>
					<CreatePartner hideModal={hideModal} onCreateFinished={onCreateFinished} />
				</UIOverlayCard>
			</UIModal>
		);
	};

	//Mark: Render functions

	return (
		<div className='regionConfig'>
			<div className='regionConfig__tabs'>
				<UIDropdownSelect
					containerClassName='regionConfig__tabs--bar'
					selectedTab={selectedTab}
					updateSelected={(selectedTab) => setSelectedTab(selectedTab)}
					tabs={[TABS.REGION, TABS.PARTNERS]}
				/>
			</div>
			<div className='regionConfig__content'>{renderTabPage(selectedTab)}</div>
			{renderAddingPartnerModal()}
			{renderCreatePackageForPartnerModal()}
			{renderDeletePartnerModal()}
		</div>
	);
}
